import React, { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { decodeCronString, getFriendlyDayName, CRON_PERIODS, CRON_MONTHS, parseSchedule } from 'utils/recurring';

import DateTime from 'components/DateTime';
import TaskGroupName from 'components/task-group-name';

const StartAt = ({ startAtDate, showTimezone, tzIana }) => {
    return (
        <span>
            <DateTime format="time">{ startAtDate }</DateTime>

            {
                showTimezone && tzIana ? (
                    <span className="control-text-additional">({ tzIana.replace(/_/g, ' ') })</span>
                ) : null
            }
        </span>
    );
}

const ResourceRepeatText = ({ type, schedule, cron, trigger, tz_iana, tz_offset, bi_weekly, short = true }: any) => {
    const { t } = useTranslation('recurring');

    const scheduleTextElement = useMemo(() => {
        if (!schedule) {
            return null;
        }

        const recur = parseSchedule(schedule, (tz_offset || 0) - (new Date().getTimezoneOffset() || 0));
        const startAtDate = new Date();

        startAtDate.setHours(0);
        startAtDate.setMinutes(recur.time / 60);

        const components = {
            StartAt: <StartAt tzIana={tz_iana} startAtDate={startAtDate} />
        };

        const daysOfWeekText = recur.dayOfWeek?.map(day => t(`date:${short ? 'weekDays_short' : 'weekDays'}.${getFriendlyDayName(day - 1)}`)).join(', ');

        switch (recur.type) {
            case 'year':
                return (
                    <Trans
                        i18nKey="recurring:recurText.year"
                        components={components}
                        count={recur.every}
                        values={{
                            month: t(`date:months.${CRON_MONTHS[recur.month]}`),
                            dayOfMonth: recur.dayOfMonth
                        }}
                    />
                );

            case 'month':
                if (recur.weekOfMonth) {
                    return (
                        <Trans
                            i18nKey="recurring:recurText.monthWeek"
                            components={components}
                            count={recur.every}
                            values={{
                                weekOfMonth: recur.dayOfMonth,
                                daysOfWeekText: daysOfWeekText
                            }}
                        />
                    );
                }

                return (
                    <Trans
                        i18nKey="recurring:recurText.monthDay"
                        components={components}
                        count={recur.every}
                        values={{
                            dayOfMonth: recur.dayOfMonth
                        }}
                    />
                );

            case 'weekOfMonth':
            case 'weekOfYear':
                return (
                    <Trans
                        i18nKey="recurring:recurText.weekOfMonth"
                        components={components}
                        count={recur.every}
                        values={{
                            daysOfWeekText: daysOfWeekText
                        }}
                    />
                );

            case 'dayOfMonth':
            case 'dayOfYear':
                return (
                    <Trans
                        i18nKey="recurring:recurText.dayOfMonth"
                        components={components}
                        count={recur.every}
                        values={{
                            daysOfWeekText: daysOfWeekText
                        }}
                    />
                );
        }

        return null;
    }, [schedule, tz_iana]);

    const cronTextElement = useMemo(() => {
        const cronProps = decodeCronString({
            cronString: cron,
            cronTZOffset: tz_offset,
            biWeekly: bi_weekly
        });
        const [hours, minutes] = cronProps.cronStartAt.split(':');
        const startAtDate = new Date();
        startAtDate.setHours(hours - 0);
        startAtDate.setMinutes(minutes - 0);

        const startAtComponent = {
            StartAt: <StartAt showTimezone={cronProps.showTimezone} tzIana={tz_iana} startAtDate={startAtDate} />
        };

        switch (cronProps.cronType) {
            case 'period':
                if (bi_weekly) {
                    return (
                        <Trans
                            i18nKey="recurring:text.biWeekly"
                            components={startAtComponent}
                            values={{
                                dayName: t(`date:weekDays.${getFriendlyDayName(cronProps.cronDayOfWeek[0])}`)
                            }}
                        />
                    );
                }

                return (
                    <Trans
                        i18nKey="recurring:text.period"
                        components={startAtComponent}
                        values={{
                            month: t(`recurring:cronPeriods.${CRON_PERIODS[cronProps.cronMonth]}`),
                            dayOfMonth: cronProps.cronDayOfMonth
                        }}
                    />
                );

            case 'year':
                return (
                    <Trans
                        i18nKey="recurring:text.year"
                        components={startAtComponent}
                        values={{
                            month: t(`date:months.${CRON_MONTHS[cronProps.cronMonth]}`),
                            dayOfMonth: cronProps.cronDayOfMonth
                        }}
                    />
                );

            case 'month':
                return (
                    <Trans
                        i18nKey="recurring:text.month"
                        components={startAtComponent}
                        values={{
                            dayOfMonth: cronProps.cronDayOfMonth
                        }}
                    />
                );
        }

        if (cronProps.cronType === 'week' || cronProps.cronDayOfWeek.length === 1) {
            return (
                <Trans
                    i18nKey="recurring:text.week"
                    components={startAtComponent}
                    values={{
                        dayName: t(`date:weekDays.${getFriendlyDayName(cronProps.cronDayOfWeek[0])}`)
                    }}
                />
            );
        }

        if (cronProps.cronDayOfWeek.length === 7) {
            return (
                <Trans
                    i18nKey="recurring:text.day"
                    components={startAtComponent}
                />
            );
        }

        return (
            <Trans
                i18nKey="recurring:text.days"
                components={startAtComponent}
                values={{
                    days: cronProps.cronDayOfWeek.map(day => t(`date:weekDays.${getFriendlyDayName(day)}`)[0]).join(', ')
                }}
            />
        );
    }, [cron, tz_iana]);

    if (type === 'status') {
        return (
            <Trans
                i18nKey="recurring:text.status"
                components={{
                    TaskGroupName: <TaskGroupName id={trigger} />
                }}
            />
        );
    }

    if (type === 'completion') {
        return <>{ t('text.completion') }</>;
    }

    if (schedule) {
        return scheduleTextElement;
    }

    if (cron) {
        return cronTextElement;
    }

    return null;
}

export default ResourceRepeatText;
